/**
 * LumaScript Editor Panel
 *
 * Context-aware editor: Project-level LumaScript when no artifact selected,
 * Artifact-level LumaScript when artifact selected
 */

import { useState } from 'react';
import Editor from '@monaco-editor/react';
import { useProjectStore } from '@/stores/projectStore';
import { useTheme } from '@/hooks/useTheme';
import { useToastStore } from '@/stores/toastStore';
import { useUserStore } from '@/stores/userStore';
import { validateLumaScript } from '@luma-factory/lumascript';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { transformApi, readStream, api } from '@/lib/api';
import { ConfirmDialog } from '../ui/ConfirmDialog';

type ConfirmAction = 'toPlan' | 'toPreview' | null;

export const LumaScriptEditorPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const { theme } = useTheme();
  const { addToast } = useToastStore();
  const activeUserId = useUserStore((state) => state.activeUserId) || 'default-user';
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [previousContent, setPreviousContent] = useState<string>('');
  const [transforming, setTransforming] = useState(false);

  // Get current project and artifact
  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  // Context detection
  const isProjectContext = !selectedArtifactId;

  // Get LumaScript content based on context
  const lumascriptContent = isProjectContext
    ? project?.stages.LUMASCRIPT?.content
    : artifact?.stages.LUMASCRIPT?.content;

  const lumascript = lumascriptContent ? JSON.parse(lumascriptContent) : null;

  if (!project) {
    return (
      <div className="panel-container">
        <div className="panel-content flex items-center justify-center h-full text-muted-foreground">
          <p className="text-sm">No project selected. Create or open a project to get started.</p>
        </div>
      </div>
    );
  }

  const handleChange = (value: string | undefined) => {
    if (value === undefined || !activeProjectId) return;

    try {
      const parsed = JSON.parse(value);
      const validation = validateLumaScript(parsed);

      if (validation.valid) {
        if (isProjectContext) {
          // Update project-level LUMASCRIPT stage
          updateProjectStage(activeProjectId, 'LUMASCRIPT', JSON.stringify(parsed, null, 2), true);
        } else if (selectedArtifactId) {
          // Update artifact-level LUMASCRIPT stage
          updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', JSON.stringify(parsed, null, 2), true);
        }
      } else {
        // TODO: Show validation errors in Problems panel
        console.error('Validation errors:', validation.errors);
      }
    } catch (error) {
      // Invalid JSON - will be caught by Monaco
    }
  };

  const handleToPlanClick = () => {
    const planContent = isProjectContext
      ? project?.stages.PLAN?.content
      : artifact?.stages.ARTIFACT_PLAN?.content;

    if (planContent && planContent.trim()) {
      setConfirmAction('toPlan');
    } else {
      executeToPlan();
    }
  };

  const handleToPreviewClick = () => {
    const bpmnContent = isProjectContext
      ? project?.stages.BPMN?.content
      : artifact?.stages.BPMN?.content;

    if (bpmnContent && bpmnContent.trim()) {
      setConfirmAction('toPreview');
    } else {
      executeToPreview();
    }
  };

  const executeToPlan = async () => {
    if (!lumascript || !activeProjectId) return;

    setTransforming(true);

    try {
      const stream = await transformApi.lumaScriptToPlan(lumascript);
      let newPlan = '';
      for await (const chunk of readStream(stream)) {
        newPlan += chunk;
      }

      if (isProjectContext) {
        // Update project-level PLAN stage
        const planStage = project?.stages.PLAN;
        setPreviousContent(planStage?.content || '');
        await updateProjectStage(activeProjectId, 'PLAN', newPlan, false);
        setActiveStage('PLAN');
        addToast('success', 'Successfully transformed LumaScript to Master Plan');
      } else if (selectedArtifactId) {
        // Update artifact-level ARTIFACT_PLAN stage
        const planStage = artifact?.stages.ARTIFACT_PLAN;
        setPreviousContent(planStage?.content || '');
        updateArtifactStage(activeProjectId, selectedArtifactId, 'ARTIFACT_PLAN', newPlan, false);
        setActiveStage('ARTIFACT_PLAN');
        addToast('success', 'Successfully transformed LumaScript to Artifact Plan');
      }
    } catch (error: any) {
      console.error('Transform error:', error);
      addToast('error', `Failed to transform: ${error.message || 'Unknown error'}`);
      // Restore previous content on error
      if (isProjectContext) {
        await updateProjectStage(activeProjectId, 'PLAN', previousContent, false);
      } else if (selectedArtifactId) {
        updateArtifactStage(activeProjectId, selectedArtifactId, 'ARTIFACT_PLAN', previousContent, false);
      }
    } finally {
      setTransforming(false);
    }
  };

  const executeToPreview = async () => {
    if (!lumascript || !activeProjectId) return;
    if (!isProjectContext && !selectedArtifactId) return;  // Artifact context needs artifact

    setTransforming(true);

    try {
      // Call BPMN generation endpoint
      const response = await api.post('/transforms/lumascript-to-bpmn', {
        lumascript,
        artifactId: selectedArtifactId,
        projectId: isProjectContext ? activeProjectId : undefined,
        userId: activeUserId,
        skillId: selectedArtifactId || activeProjectId,
        options: {
          layout: 'horizontal',
          spacing: 100,
          canvasWidth: 2000,
          canvasHeight: 1500
        }
      });

      const { jobId } = response.data;

      // Poll for job completion
      let attempts = 0;
      const maxAttempts = 60; // 60 seconds max (BPMN generation can take 30+ seconds)
      const pollInterval = 1000; // 1 second

      const pollJob = async (): Promise<void> => {
        if (attempts >= maxAttempts) {
          throw new Error('Preview generation timed out after 60 seconds');
        }

        attempts++;
        const jobResponse = await api.get(`/jobs/${jobId}`);
        const { status, result, error } = jobResponse.data;

        if (status === 'completed' && result) {
          // Store BPMN based on context
          if (isProjectContext) {
            await updateProjectStage(activeProjectId, 'BPMN', result, false);
          } else if (selectedArtifactId) {
            updateArtifactStage(activeProjectId, selectedArtifactId, 'BPMN', result, false);
          }
          setActiveStage('BPMN');
          addToast('success', 'Preview generated successfully!');
        } else if (status === 'failed') {
          throw new Error(error || 'Preview generation failed');
        } else {
          // Still processing, poll again
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          await pollJob();
        }
      };

      await pollJob();
    } catch (error: any) {
      const serverMessage = error.response?.data?.error || error.message || 'Unknown error';
      if (serverMessage.toLowerCase().includes('no llm')) {
        addToast('error', 'Preview failed: add an API key and active provider, then try again.');
      } else {
        addToast('error', `Failed to generate Preview: ${serverMessage}`);
      }
    } finally {
      setTransforming(false);
    }
  };

  const handleConfirm = () => {
    setConfirmAction(null);
    if (confirmAction === 'toPlan') {
      executeToPlan();
    } else if (confirmAction === 'toPreview') {
      executeToPreview();
    }
  };

  const handleCancel = () => {
    setConfirmAction(null);
  };

  return (
    <div className="panel-container">
      {/* Conversion Buttons Bar */}
      <div className="h-8 border-b border-border bg-background flex items-center justify-center gap-2 px-4">
        {transforming ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Transforming...</span>
          </div>
        ) : (
          <>
            <button
              onClick={handleToPlanClick}
              disabled={!lumascript || transforming}
              className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3 h-3" />
              To Plan
            </button>
            <button
              onClick={handleToPreviewClick}
              disabled={!lumascript || transforming}
              className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              To Preview
              <ArrowRight className="w-3 h-3" />
            </button>
          </>
        )}
      </div>

      <div className="panel-content">
        <Editor
          key={theme}
          height="100%"
          language="json"
          value={lumascript ? JSON.stringify(lumascript, null, 2) : ''}
          onChange={handleChange}
          theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
          options={{
            minimap: { enabled: false },
            lineNumbers: 'on',
            wordWrap: 'on',
            formatOnPaste: true,
            formatOnType: true,
            fontSize: 14,
            padding: { top: 16, bottom: 16 }
          }}
        />
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmAction !== null}
        title="Overwrite Existing Content?"
        message={
          confirmAction === 'toPlan'
            ? 'This will overwrite the existing Plan content. Are you sure?'
            : 'This will overwrite the existing Preview content. Are you sure?'
        }
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};
